import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ImageIcon,
  Camera,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Play,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export default function GuardianGallery() {
  const { t, language } = useI18n();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [lightbox, setLightbox] = useState(null); // { album, media, index }

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["gallery-parent"],
    queryFn: async () => unwrap(await api.get("/gallery/parent")),
  });

  const { data: consents, isLoading: consentsLoading, isError: consentsError, error: consentsErrorDetail } = useQuery({
    queryKey: ["gallery-consent"],
    queryFn: async () => unwrap(await api.get("/gallery/consent")),
  });

  const consentMutation = useMutation({
    mutationFn: async ({ studentId, canViewPhotos }) => unwrap(await api.patch("/gallery/consent", {
      student_id: studentId,
      can_view_photos: canViewPhotos,
    })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery-parent"] });
      queryClient.invalidateQueries({ queryKey: ["gallery-consent"] });
      toast.success(t("guardian.galleryConsentUpdated"));
    },
    onError: (mutationError) => toast.error(errMsg(mutationError)),
  });

  const filteredAlbums = (data || []).filter((album) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      album.title?.toLowerCase().includes(term) ||
      album.description?.toLowerCase().includes(term)
    );
  });

  const totalMedia = (data || []).reduce(
    (sum, album) => sum + (album.media?.length || 0),
    0
  );

  const openLightbox = (album, media, index) => {
    setLightbox({ album, media, index });
  };

  const closeLightbox = () => {
    setLightbox(null);
  };

  const navigateLightbox = (direction) => {
    if (!lightbox) return;
    const newIndex = lightbox.index + direction;
    if (newIndex < 0 || newIndex >= lightbox.media.length) return;
    setLightbox({ ...lightbox, index: newIndex });
  };

  return (
    <div className="space-y-6" data-testid="guardian-gallery-page">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 text-white p-6 md:p-8 shadow-lg">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.2),transparent_50%)]" />
        <div className="relative flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <Camera className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl md:text-3xl font-bold">
              {t("guardian.activityPhotosTitle")}
            </h1>
            <p className="text-white/90 text-sm md:text-base mt-1">
              {t("guardian.activityPhotosSubtitle")}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                {t("guardian.albumsCount", { count: (data || []).length })}
              </Badge>
              <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                {t("guardian.mediaItemsCount", { count: totalMedia })}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Consent Settings */}
      {consentsLoading && (
        <Card className="border-[var(--subtle)]" data-testid="gallery-consent-loading">
          <CardContent className="p-4">
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      )}
      {consentsError && (
        <Card className="border-[var(--error)]/30" data-testid="gallery-consent-error">
          <CardContent className="p-4 text-sm text-[var(--error)]" role="alert">
            {errMsg(consentsErrorDetail)}
          </CardContent>
        </Card>
      )}
      {!consentsLoading && !consentsError && !!consents?.length && (
        <Card className="border-[var(--subtle)]" data-testid="gallery-consent-settings">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-display flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              {t("guardian.galleryConsentTitle")}
            </CardTitle>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {t("guardian.galleryConsentHint")}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {consents.map((consent) => {
              const name = `${consent.first_name || ""} ${consent.last_name || ""}`.trim() || consent.student_id;
              return (
                <div key={consent.student_id} className="flex items-center justify-between gap-3 p-2 rounded-md hover:bg-[var(--bg)]">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[var(--text-primary)] truncate">
                      {name}
                    </div>
                    <div className="text-xs text-[var(--text-muted)]">
                      {consent.can_view_photos !== false
                        ? t("guardian.galleryConsentOn")
                        : t("guardian.galleryConsentOff")}
                    </div>
                  </div>
                  <Switch
                    checked={consent.can_view_photos !== false}
                    disabled={consentMutation.isPending}
                    onCheckedChange={(checked) =>
                      consentMutation.mutate({ studentId: consent.student_id, canViewPhotos: checked })
                    }
                    aria-label={`${name} ${t("guardian.galleryConsentTitle")}`}
                    data-testid={`gallery-consent-${consent.student_id}`}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Search */}
      {(data || []).length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-muted)]" />
          <Input
            type="text"
            placeholder={t("guardian.gallerySearchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="gallery-search"
          />
        </div>
      )}

      {/* Loading / Error / Content */}
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : isError ? (
        <Card className="border-[var(--error)]/30">
          <CardContent className="py-10 text-center text-sm text-[var(--error)]">
            {errMsg(error)}
          </CardContent>
        </Card>
      ) : filteredAlbums.length === 0 ? (
        <Card className="border-[var(--subtle)]">
          <CardContent className="py-12 text-center text-[var(--text-muted)]">
            <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
            {searchTerm ? t("guardian.galleryNoSearchResults") : t("nursery.noAlbumsYet")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6 stagger" data-testid="gallery-albums">
          {filteredAlbums.map((album) => (
            <Card key={album.id} className="border-[var(--subtle)] overflow-hidden" data-testid={`parent-album-${album.id}`}>
              <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base font-display truncate flex items-center gap-2">
                      <ImageIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      {album.title}
                    </CardTitle>
                    {album.description && (
                      <p className="text-xs text-[var(--text-muted)] mt-1">{album.description}</p>
                    )}
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      {album.created_at && formatDate(album.created_at, language)}
                    </p>
                  </div>
                  <Badge className="flex-shrink-0 bg-purple-100 text-purple-700 border-0">
                    {t("guardian.mediaCount", { count: album.media?.length || 0 })}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {album.media?.length > 0 ? (
                  <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
                    {album.media.map((media, idx) => (
                      <button
                        key={media.id}
                        onClick={() => openLightbox(album, album.media, idx)}
                        className="group relative aspect-square overflow-hidden rounded-md border border-[var(--subtle)] bg-[var(--bg)] hover:shadow-md transition-all"
                        data-testid={`parent-media-${media.id}`}
                      >
                        {media.kind === "video" ? (
                          <>
                            <video
                              src={media.url}
                              className="h-full w-full object-cover"
                              preload="metadata"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                              <div className="h-10 w-10 rounded-full bg-white/90 flex items-center justify-center">
                                <Play className="h-5 w-5 text-purple-600 fill-current ml-0.5" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <img
                            src={media.thumbnail_url || media.url}
                            alt={media.caption || album.title}
                            loading="lazy"
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform"
                          />
                        )}
                        {media.caption && (
                          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-white text-xs line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {media.caption}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-[var(--text-muted)]">
                    {t("guardian.galleryNoMediaInAlbum")}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && closeLightbox()}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-0">
          <DialogTitle className="sr-only">
            {lightbox?.media?.[lightbox.index]?.caption ||
              lightbox?.album?.title ||
              t("guardian.galleryLightbox")}
          </DialogTitle>
          {lightbox && (
            <div className="relative" data-testid="gallery-lightbox">
              <Button
                variant="ghost"
                size="sm"
                onClick={closeLightbox}
                className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
                data-testid="lightbox-close"
              >
                <X className="h-5 w-5" />
              </Button>
              {lightbox.media[lightbox.index]?.kind === "video" ? (
                <video
                  controls
                  autoPlay
                  className="w-full max-h-[80vh] bg-black"
                  key={lightbox.media[lightbox.index].id}
                >
                  <source src={lightbox.media[lightbox.index].url} />
                </video>
              ) : (
                <img
                  src={lightbox.media[lightbox.index]?.url}
                  alt={lightbox.media[lightbox.index]?.caption || lightbox.album.title}
                  className="w-full max-h-[80vh] object-contain"
                />
              )}
              {lightbox.media.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigateLightbox(-1)}
                    disabled={lightbox.index === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 disabled:opacity-30"
                    data-testid="lightbox-prev"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigateLightbox(1)}
                    disabled={lightbox.index === lightbox.media.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 disabled:opacity-30"
                    data-testid="lightbox-next"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                </>
              )}
              {lightbox.media[lightbox.index]?.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent text-white text-sm">
                  {lightbox.media[lightbox.index].caption}
                  <div className="text-xs text-white/70 mt-1">
                    {lightbox.index + 1} / {lightbox.media.length}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
